import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import {
  LinkedinLogo,
  InstagramLogo,
  TwitterLogo,
} from '@phosphor-icons/react';
import useLoginModal from '../../../login/useLoginModal';
import './style';
import { useLocation } from 'react-router-dom';
import LoginModal from '../../../login/loginModal'
import { isValidURL } from '../../../../utils/commonUtils';
import { CALENDAR_LINK } from '../../../../constants/common';

const nonFooterRoutes = [
  'sell-tickets',
  'add-category',
  'attendees',
  'checkout',
  'event/create',
  'event/edit',
  'event/create',
  'event/dashboard',
];
const eventIdPattern = /^\/event\/[a-f0-9]{24}$/;

const Footer = () => {
  const loginModal = useLoginModal();
  const navigate = useNavigate();
  const location = useLocation();
  const { setQR } = useLoginModal();
  const [showFooter, setShowFooter] = useState(true);

  useEffect(() => {
    const shouldNotShowFooter =
      nonFooterRoutes.some((route) => location.pathname?.includes(route)) ||
      eventIdPattern.test(location.pathname);
    setShowFooter(!shouldNotShowFooter);
  }, [location.pathname]);

  const { userData } = useSelector((state) => state.app);
  // Define your links as an array of objects with an optional newTab property
  const quickLinks = [
    { name: 'Browse events', path: '/all-events' },
    { name: 'Re-sell Tickets', path: '/sell-tickets', requireLogin: true },
    {
      name: 'List your event',
      path: '/landing',
      newTab: true,
    },
    { name: 'Request an Event', path: '/request' },
    { name: 'Scanner App', path: '/events', mobilePath: 'https://scanner.takemytickets.co.in/', requireLogin: true },
    {
      name: 'Schedule a Call',
      onClick: () =>
        window.open('https://cal.com/vasav-shah', '_blank', 'noopener,noreferrer'),
    },
  ];

  const organizeEvents = [
    {
      name: 'College Fests & Culturals',
      path: '/landing',
      newTab: true,
    },
    {
      name: 'Department Symposiums',
      path: '/landing',
      newTab: true,
    },
    {
      name: 'Alumni Meetups',
      path: '/landing',
      newTab: true,
    },
    {
      name: 'Local Workshops',
      path: '/landing',
      newTab: true,
    },
    {
      name: 'Community Gatherings',
      path: '/landing',
      newTab: true,
    },
    {
      name: 'Entertainment Live Events',
      path: '/landing',
      newTab: true,
    },
    {
      name: 'Concerts & Stand-Up Shows',
      path: '/landing',
      newTab: true,
    },
  ];

  const companyLinks = [
    {
      name: 'Advertise with Us',
      path: 'mailto:invest@takemytickets.co.in?subject=Interested%20in%20investing%20in%20Take%20My%20Tickets&body=Hello%2C%0D%0AI%20am%20interested%20in%20learning%20more%20about%20investment%20opportunities%20with%20Take%20My%20Tickets.%20Please%20provide%20me%20with%20further%20details.%0D%0AThank%20you',
    },
    {
      name: 'For Investors',
      path: 'mailto:invest@takemytickets.co.in?subject=Interested%20in%20investing%20in%20Take%20My%20Tickets&body=Hello%2C%0D%0AI%20am%20interested%20in%20learning%20more%20about%20investment%20opportunities%20with%20Take%20My%20Tickets.%20Please%20provide%20me%20with%20further%20details.%0D%0AThank%20you',
    },
    { name: 'Terms and Conditions', path: '/terms-and-conditions' },
    { name: 'Privacy policy', path: '/privacy-policy' },
    { name: 'Refund policy', path: '/refund-policy' },
    { name: 'Contact us', path: 'mailto:rescue@takemytickets.co.in' },
    { name: 'Book a Call', path: CALENDAR_LINK, newTab: true }
  ];

  const socialLinks = [
    {
      name: 'LinkedIn',
      path: 'https://www.linkedin.com/company/take-my-tickets',
      newTab: true,
      logo: <LinkedinLogo size={20}
        weight="fill"
        className="mr-2" />,
    },
    {
      name: 'Twitter',
      path: 'https://x.com/takemy_tickets',
      newTab: true,
      logo: <TwitterLogo size={20}
        weight="fill"
        className="mr-2" />,
    },
    {
      name: 'Instagram',
      path: 'https://www.instagram.com/takemytickets.co.in/',
      newTab: true,
      logo: <InstagramLogo
        size={20}
        weight="fill"
        className="mr-2" />,
    },
  ];

  const onClickLink = (link, e) => {
    if (link.name === "Scanner App") {
      setQR(false);
    }

    const navigateTo = () => {
      const path = window.innerWidth < 650 && link.mobilePath ? link.mobilePath : link.path;
      if (isValidURL(path)) {
        window.open(path, '_blank');
      } else navigate(path);
    };

    if (link.requireLogin) {
      e.preventDefault();
      e.stopPropagation();
      if (!userData?.email) {
        loginModal.onOpen();
        loginModal.callback.current = navigateTo;
        return;
      }
    }
    navigateTo();
  };

  return (
    <footer className={`footerContainer ${showFooter ? '' : 'hidden'}`}>
      <Helmet>
        <meta
          name="description"
          content="Explore and organize a wide range of events such as College Fests, Culturals, Department Symposiums, Alumni Meetups, Local Workshops, Community Gatherings, Entertainment Live Events, Concerts, and Stand-Up Shows. Take My Tickets offers a platform for all your event needs."
        />

        <meta
          name="keywords"
          content="College Fests, Culturals, Department Symposiums, Alumni Meetups, Local Workshops, Community Gatherings, Entertainment Live Events, Concerts, Stand-Up Shows, event listing platform, event organization, online ticketing"
        />

        <meta
          name="twitter:title"
          content="Your Event Listing Platform | Free Event Listings & Ticket Sales"
        />
        <meta
          name="twitter:description"
          content="List your event for free on India's top event platform. Create event pages, sell tickets, and find events easily."
        />
        <meta name="twitter:image"
          content="assets/images/logo-primary.svg" />
      </Helmet>
      <div className="innerContainer">
        <div className="sponsorContainer">
          {window.innerWidth > 650 && (
            <img
              src="/assets/images/logo-secondary.svg"
              alt="TMT-secondary"
              className="w-[92px] height-[60px]"
            />
          )}
          <div className="img-quickLinks">
            <p className="title">Supported by</p>
            <div className="img-body">
              <img src="/assets/images/zoho.svg"
                alt="Zoho Startups" />
              <img src="/assets/images/mfs.png"
                alt="AWS Startups"
                className="w-[135px] height-[120px] bg-white border-2 border-gray-200 rounded-md" />
              <img src="/assets/images/startupindia.png"
                alt="AWS Startups"
                className="w-[135px] height-[80px] bg-white border-2 border-gray-200 rounded-md" />
            </div>
          </div>
        </div>

        <div className="quicklinkContainer">
          {window.innerWidth < 650 && (
            <img
              src="/assets/images/logo-secondary.svg"
              alt="TMT-secondary"
              className="w-[92px] height-[60px]"
            />
          )}
          {renderQuickLinks('Quick Links', quickLinks, onClickLink)}
          {renderQuickLinks('Organize events', organizeEvents, onClickLink)}
          {renderQuickLinks('Company', companyLinks, onClickLink)}
          {renderQuickLinks('Follow us', socialLinks, onClickLink)}
        </div>
      </div>
      <div className="copyrights">
        <hr className="divider" />
        <p className="text">© 2025 Take My Tickets. All rights reserved.</p>
      </div>
    </footer>
  );
};

// const renderQuickLinks = (title, links, onClick) => (
//   <>
//   <div className="quickLinks">
//     <p className="title">{title}</p>
//     <div className="body">
//       {links.map((link, index) => (
//         <Link
//           to={link.path}
//           className="link"
//           key={index}
//           onClick={(e) => onClick(link, e)}
//           target={link.newTab ? '_blank' : '_self'} // Conditionally set target
//           rel={link.newTab ? 'noopener noreferrer' : undefined} // Add rel for security when opening new tabs
//         >
//           <span>{link.logo && link.logo}</span>
//           {link.name}
//         </Link>
//       ))}
//     </div>
//   </div>
//   </>
// );
// ✅ Renders quick links section
const renderQuickLinks = (title, links, onClick) => (
  <div className="quickLinks">
    <p className="title">{title}</p>
    <div className="body">
      {links.map((link, index) => {
        // If there's an onClick handler, render a <button> instead of a <Link>
        if (link.onClick) {
          return (
            <Link
              key={index}
              className="link"
              onClick={(e) => {
                e.preventDefault();
                onClick?.(link, e);
                link.onClick();
              }}
            >
              {link.logo && <span>{link.logo}</span>}
              {link.name}
            </Link>
          );
        }

        // Otherwise render a normal <Link>
        return (
          <Link
            key={index}
            to={link.path}
            className="link"
            onClick={(e) => onClick?.(link, e)}
            target={link.newTab ? '_blank' : '_self'}
            rel={link.newTab ? 'noopener noreferrer' : undefined}
          >
            {link.logo && <span>{link.logo}</span>}
            {link.name}
          </Link>
        );
      })}
    </div>
  </div>
);

export default Footer;
